import { bcs } from '@mysten/bcs';
import { SUI_SYSTEM_ADDRESS } from '@mysten/sui/utils';
/** FungibleStakedSui.value is pool tokens, not DGC mist — convert via pool exchange rate. */
const FSS_DATA_KEY_TYPE = `${SUI_SYSTEM_ADDRESS}::staking_pool::FungibleStakedSuiDataKey`;
/**
 * Mirror of `staking_pool::calculate_fungible_staked_sui_withdraw_amount`.
 * Principal = what `redeem_fungible_staked_sui` pays after unlock.
 * Rewards = what `redeem_fungible_staked_sui_rewards_only` pays now.
 */
export function calculateFungibleWithdrawAmounts(rate, fungibleStakedSuiValue, poolPrincipalMist, poolTotalSupply) {
    if (poolTotalSupply === 0n || fungibleStakedSuiValue === 0n) {
        return { principalMist: 0n, rewardsMist: 0n };
    }
    const totalSuiAmount = poolTokensToCpcMist(poolTotalSupply, rate);
    const principalCap = poolPrincipalMist < totalSuiAmount ? poolPrincipalMist : totalSuiAmount;
    const totalRewards = totalSuiAmount - principalCap;
    return {
        principalMist: (fungibleStakedSuiValue * principalCap) / poolTotalSupply,
        rewardsMist: (fungibleStakedSuiValue * totalRewards) / poolTotalSupply,
    };
}
/** Rewards leg only — same as `redeem_fungible_staked_sui_rewards_only`. */
export function calculateFungibleRewardsWithdrawMist(rate, fungibleStakedSuiValue, poolPrincipalMist, poolTotalSupply) {
    return calculateFungibleWithdrawAmounts(rate, fungibleStakedSuiValue, poolPrincipalMist, poolTotalSupply).rewardsMist;
}
/**
 * On-chain `rewards_only` pays a slice each call. One user click repeats until
 * dust so leftover is not left for a second click (and extra gas).
 */
export const MAX_FUNGIBLE_REWARD_REDEEMS_PER_TX = 32;
/** Hide / skip claim when remaining is below this (0.001 RWAX) — not worth gas. */
export const CLAIM_REWARDS_DUST_MIST = 1000000n;
/**
 * Repeat `redeem_fungible_staked_sui_rewards_only` against the same certificate
 * until this epoch's surplus for it is gone (or the PTB cap). Rate does not
 * change until the next epoch, so leftover after one redeem is still claimable now.
 */
export function planFungibleRewardsDrain(rate, fungibleStakedSuiValue, poolPrincipalMist, poolTotalSupply, maxRedeems = MAX_FUNGIBLE_REWARD_REDEEMS_PER_TX) {
    let value = fungibleStakedSuiValue;
    let supply = poolTotalSupply;
    let totalRewardsMist = 0n;
    let redeemCount = 0;
    const cap = Math.max(0, Math.min(64, maxRedeems));
    while (redeemCount < cap && value > 0n && supply > 0n) {
        const rewardsMist = calculateFungibleRewardsWithdrawMist(rate, value, poolPrincipalMist, supply);
        if (rewardsMist <= 0n)
            break;
        const burned = cpcMistToPoolTokens(rewardsMist, rate);
        if (burned <= 0n || burned > value)
            break;
        totalRewardsMist += rewardsMist;
        value -= burned;
        supply -= burned;
        redeemCount += 1;
    }
    return { totalRewardsMist, redeemCount, remainingValue: value };
}
export function poolTokensToCpcMist(poolTokenAmount, rate) {
    if (rate.poolTokenAmount === 0n || rate.suiAmount === 0n) {
        return poolTokenAmount;
    }
    return (rate.suiAmount * poolTokenAmount) / rate.poolTokenAmount;
}
/** Mirror of `PoolTokenExchangeRate::get_token_amount`. */
export function cpcMistToPoolTokens(mist, rate) {
    if (rate.suiAmount === 0n || rate.poolTokenAmount === 0n) {
        return mist;
    }
    return (rate.poolTokenAmount * mist) / rate.suiAmount;
}
function fungibleStakedSuiDataKeyBcs() {
    // On-chain key is `FungibleStakedSuiDataKey { dummy_field: bool }` (not empty struct).
    const key = bcs.struct('FungibleStakedSuiDataKey', {
        dummy_field: bcs.bool(),
    });
    return key.serialize({ dummy_field: false }).toBytes();
}
const SUI_SYSTEM_STATE_OBJECT_ID = '0x0000000000000000000000000000000000000000000000000000000000000005';
function tableInnerId(fields, key) {
    const node = fields[key];
    if (!node || typeof node !== 'object')
        return null;
    const inner = node.fields;
    return inner?.id?.id ?? null;
}
function asMoveFields(node) {
    if (!node || typeof node !== 'object')
        return undefined;
    const o = node;
    if (o.fields && typeof o.fields === 'object') {
        return o.fields;
    }
    return o;
}
function moveUid(node) {
    if (typeof node === 'string' && node.startsWith('0x'))
        return node;
    const f = asMoveFields(node);
    const id = f?.id ?? node?.id;
    if (typeof id === 'string' && id)
        return id;
    if (id && typeof id === 'object') {
        const inner = id.id;
        if (typeof inner === 'string' && inner)
            return inner;
    }
    return null;
}
function normalizeObjectId(id) {
    return id.replace(/^0x/, '').toLowerCase();
}
function parsePoolTokenExchangeRateBcs(bytes) {
    if (bytes.length < 16)
        return null;
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    return {
        suiAmount: view.getBigUint64(0, true),
        poolTokenAmount: view.getBigUint64(8, true),
    };
}
function parseExchangeRateFields(fields) {
    if (!fields)
        return null;
    const sui = fields.sui_amount ?? fields.suiAmount;
    const tok = fields.pool_token_amount ?? fields.poolTokenAmount;
    if (sui == null || tok == null)
        return null;
    return { suiAmount: BigInt(String(sui)), poolTokenAmount: BigInt(String(tok)) };
}
function parseRateFromDynamicFieldValue(value) {
    if (!value || typeof value !== 'object')
        return null;
    const v = value;
    if (v.bcs) {
        const bytes = typeof v.bcs === 'string'
            ? Uint8Array.from(atob(v.bcs), (c) => c.charCodeAt(0))
            : v.bcs;
        const fromBcs = parsePoolTokenExchangeRateBcs(bytes);
        if (fromBcs)
            return fromBcs;
    }
    const nested = v.value?.fields ?? v.fields;
    const direct = parseExchangeRateFields(nested);
    if (direct)
        return direct;
    return parseExchangeRateFields(v.fields);
}
export { fetchFungiblePoolData };
async function fetchRateFromTableDynamicField(client, tableId, epoch) {
    for (let e = epoch; e >= 0; e--) {
        try {
            if (client.getDynamicFieldObject) {
                const res = await client.getDynamicFieldObject({
                    parentId: tableId,
                    name: { type: 'u64', value: String(e) },
                });
                const rate = parseRateFromDynamicFieldValue(res.data?.content?.fields);
                if (rate)
                    return rate;
            }
            else if (client.getDynamicField) {
                const df = await client.getDynamicField({
                    parentId: tableId,
                    name: { type: 'u64', bcs: bcs.u64().serialize(e).toBytes() },
                });
                const rate = parseRateFromDynamicFieldValue(df.dynamicField?.value ?? df.data?.content?.fields);
                if (rate)
                    return rate;
                const fieldsRate = parseExchangeRateFields(df.data?.content?.fields);
                if (fieldsRate)
                    return fieldsRate;
            }
        }
        catch {
            continue;
        }
    }
    return null;
}
async function fetchRateFromValidatorExchangeTable(client, poolId, epoch) {
    if (!client.getLatestSuiSystemState)
        return null;
    const state = await client.getLatestSuiSystemState();
    const validators = state.activeValidators ?? [];
    const v = validators.find((x) => x.stakingPoolId === poolId);
    if (!v?.exchangeRatesId)
        return null;
    return fetchRateFromTableDynamicField(client, v.exchangeRatesId, epoch);
}
async function fetchPoolExchangeRateAtEpoch(client, poolId, epoch) {
    // Pool object is often not readable via getObject on local DGC nodes; validator table is reliable.
    const fromValidator = await fetchRateFromValidatorExchangeTable(client, poolId, epoch);
    if (fromValidator)
        return fromValidator;
    try {
        const poolRes = await client.getObject({ id: poolId, options: { showContent: true } });
        const poolFields = poolRes.data?.content?.fields;
        const tableId = poolFields ? tableInnerId(poolFields, 'exchange_rates') : null;
        if (tableId) {
            return fetchRateFromTableDynamicField(client, tableId, epoch);
        }
    }
    catch {
        /* pool object missing */
    }
    return null;
}
function u64Field(fields, key) {
    const v = fields?.[key];
    if (v == null)
        return null;
    return BigInt(String(v));
}
function parseFungiblePoolDataFields(fields) {
    if (!fields)
        return null;
    const principalMist = (() => {
        const principal = fields.principal;
        if (principal == null)
            return null;
        if (typeof principal === 'string' || typeof principal === 'number') {
            return BigInt(String(principal));
        }
        if (typeof principal !== 'object')
            return null;
        const p = principal;
        const v = p.fields?.value ?? p.value;
        return v == null ? null : BigInt(String(v));
    })();
    const lockUntilEpoch = u64Field(fields, 'lock_until_epoch');
    if (principalMist == null || lockUntilEpoch == null)
        return null;
    const totalSupply = u64Field(fields, 'total_supply') ?? 0n;
    const stakeActivationEpoch = u64Field(fields, 'stake_activation_epoch');
    return { principalMist, lockUntilEpoch, stakeActivationEpoch, totalSupply };
}
function poolDataFromFieldWrapper(fields) {
    if (!fields)
        return null;
    const valueNode = fields.value;
    if (valueNode?.fields) {
        return parseFungiblePoolDataFields(valueNode.fields);
    }
    return parseFungiblePoolDataFields(fields);
}
async function readPoolDataFromDynamicObjectField(client, parentId) {
    if (client.getDynamicFieldObject) {
        try {
            const res = await client.getDynamicFieldObject({
                parentId,
                name: { type: FSS_DATA_KEY_TYPE, value: { dummy_field: false } },
            });
            const d = poolDataFromFieldWrapper(res.data?.content?.fields);
            if (d != null)
                return d;
        }
        catch {
            /* */
        }
    }
    if (!client.getDynamicObjectField)
        return null;
    const keyBcs = fungibleStakedSuiDataKeyBcs();
    const { data } = await client.getDynamicObjectField({
        parentId,
        name: { type: FSS_DATA_KEY_TYPE, bcs: keyBcs },
    });
    return poolDataFromFieldWrapper(data?.content?.fields);
}
async function readPoolDataFromDynamicField(client, parentId) {
    if (client.getDynamicFieldObject) {
        try {
            const res = await client.getDynamicFieldObject({
                parentId,
                name: { type: FSS_DATA_KEY_TYPE, value: { dummy_field: false } },
            });
            const d = poolDataFromFieldWrapper(res.data?.content?.fields);
            if (d != null)
                return d;
        }
        catch {
            /* */
        }
    }
    if (!client.getDynamicField)
        return null;
    const keyBcs = fungibleStakedSuiDataKeyBcs();
    const df = await client.getDynamicField({
        parentId,
        name: { type: FSS_DATA_KEY_TYPE, bcs: keyBcs },
    });
    return poolDataFromFieldWrapper(df.dynamicField?.value?.fields ??
        df.data?.content?.fields);
}
async function readFssDataFromFieldObjectId(client, fieldObjectId) {
    const fieldRes = await client.getObject({
        id: fieldObjectId,
        options: { showContent: true },
    });
    const data = poolDataFromFieldWrapper(fieldRes.data?.content?.fields);
    return data != null && data.principalMist > 0n ? data : null;
}
function effectObjectIds(tx) {
    const out = [];
    for (const row of [...(tx.effects?.created ?? []), ...(tx.effects?.mutated ?? [])]) {
        const id = row.reference?.objectId ?? row.objectId;
        if (id)
            out.push(id);
    }
    return out;
}
async function findFssDataFieldObjectIdInTx(client, digest) {
    if (!client.getTransactionBlock)
        return null;
    const tx = await client.getTransactionBlock({
        digest,
        options: { showObjectChanges: true, showEffects: true },
    });
    for (const ch of tx.objectChanges ?? []) {
        // After rewards_only the pool Field is mutated, not created (convert tx creates it).
        if (ch.objectId && ch.objectType?.includes('FungibleStakedSuiData')) {
            return ch.objectId;
        }
    }
    // This fork often omits objectChanges; Field id is still in effects.created/mutated.
    for (const id of effectObjectIds(tx)) {
        try {
            const obj = await client.getObject({
                id,
                options: { showType: true, showContent: true },
            });
            const typ = obj.data?.type ?? obj.data?.content?.type ?? '';
            if (typ.includes('FungibleStakedSuiData'))
                return id;
        }
        catch {
            /* not this object */
        }
    }
    return null;
}
function parseConvertOriginFromEvents(events) {
    for (const ev of events ?? []) {
        if (!ev.type?.includes('ConvertingToFungibleStakedSuiEvent'))
            continue;
        const j = ev.parsedJson ?? {};
        const principal = j.staked_sui_principal_amount;
        if (principal == null)
            continue;
        const minted = j.fungible_staked_sui_amount;
        const epoch = j.stake_activation_epoch;
        return {
            principalMist: BigInt(String(principal)),
            mintedTokens: minted != null ? BigInt(String(minted)) : 0n,
            stakeActivationEpoch: epoch != null ? BigInt(String(epoch)) : 0n,
        };
    }
    return null;
}
/** Locked principal at convert — does not change when rewards are claimed. */
export async function fetchFungibleConvertOrigin(client, fungibleStakedSuiId) {
    if (!client.getTransactionBlock)
        return null;
    const fssRes = await client.getObject({
        id: fungibleStakedSuiId,
        options: { showPreviousTransaction: true },
    });
    let digest = fssRes.data?.previousTransaction;
    for (let i = 0; i < 16 && digest; i++) {
        try {
            const tx = await client.getTransactionBlock({
                digest,
                options: { showEvents: true, showEffects: true },
            });
            const origin = parseConvertOriginFromEvents(tx.events);
            if (origin != null && origin.principalMist > 0n)
                return origin;
            digest = tx.effects?.previousTransaction;
        }
        catch {
            break;
        }
    }
    return null;
}
async function fetchFungiblePoolDataViaFssConvertTx(client, fungibleStakedSuiId) {
    if (!client.getTransactionBlock)
        return null;
    const fssRes = await client.getObject({
        id: fungibleStakedSuiId,
        options: { showContent: true, showPreviousTransaction: true },
    });
    let digest = fssRes.data?.previousTransaction;
    if (!digest)
        return null;
    for (let i = 0; i < 12 && digest; i++) {
        const fieldId = await findFssDataFieldObjectIdInTx(client, digest);
        if (fieldId) {
            const live = await readFssDataFromFieldObjectId(client, fieldId);
            if (live != null)
                return live;
        }
        try {
            const tx = await client.getTransactionBlock({
                digest,
                options: { showEffects: true },
            });
            digest = tx.effects?.previousTransaction;
        }
        catch {
            break;
        }
    }
    return null;
}
async function fetchPoolExtraFieldsBagIdFromSystem(client, poolId) {
    if (!client.getDynamicFieldObject)
        return null;
    try {
        let version = '2';
        try {
            const sys = await client.getObject({
                id: SUI_SYSTEM_STATE_OBJECT_ID,
                options: { showContent: true },
            });
            const v = sys.data?.content?.fields?.version;
            if (v != null)
                version = String(v);
        }
        catch {
            /* keep 2 */
        }
        const inner = await client.getDynamicFieldObject({
            parentId: SUI_SYSTEM_STATE_OBJECT_ID,
            name: { type: 'u64', value: version },
        });
        const wrap = inner.data?.content?.fields;
        const innerFields = asMoveFields(wrap?.value) ?? asMoveFields(wrap);
        const validators = asMoveFields(innerFields?.validators);
        const active = validators?.active_validators;
        if (!Array.isArray(active))
            return null;
        const want = normalizeObjectId(poolId);
        for (const v of active) {
            const sp = asMoveFields(asMoveFields(v)?.staking_pool);
            if (!sp)
                continue;
            const pid = moveUid(sp);
            if (!pid || normalizeObjectId(pid) !== want)
                continue;
            return moveUid(asMoveFields(sp.extra_fields));
        }
    }
    catch {
        return null;
    }
    return null;
}
async function fetchFungiblePoolData(client, poolId, fungibleStakedSuiId) {
    try {
        const bagId = await fetchPoolExtraFieldsBagIdFromSystem(client, poolId);
        if (bagId) {
            const d = await readPoolDataFromDynamicField(client, bagId);
            if (d != null)
                return d;
            const d2 = await readPoolDataFromDynamicObjectField(client, bagId);
            if (d2 != null)
                return d2;
        }
    }
    catch {
        /* */
    }
    try {
        const poolRes = await client.getObject({ id: poolId, options: { showContent: true } });
        const bagId = tableInnerId(poolRes.data?.content?.fields ?? {}, 'extra_fields');
        if (bagId) {
            const d = await readPoolDataFromDynamicObjectField(client, bagId);
            if (d != null)
                return d;
        }
    }
    catch {
        /* */
    }
    try {
        const d = await readPoolDataFromDynamicObjectField(client, poolId);
        if (d != null)
            return d;
    }
    catch {
        /* */
    }
    try {
        const d = await readPoolDataFromDynamicField(client, poolId);
        if (d != null)
            return d;
    }
    catch {
        /* suix_getDynamicFieldObject-style parent */
    }
    if (fungibleStakedSuiId) {
        return fetchFungiblePoolDataViaFssConvertTx(client, fungibleStakedSuiId);
    }
    return null;
}
export async function estimateFungibleStakedCpc(client, poolId, poolTokenValue, epoch, fungibleStakedSuiId) {
    const poolTokenAmount = BigInt(poolTokenValue);
    const [rate, poolData, convertOrigin] = await Promise.all([
        fetchPoolExchangeRateAtEpoch(client, poolId, epoch),
        fetchFungiblePoolData(client, poolId, fungibleStakedSuiId),
        fungibleStakedSuiId ? fetchFungibleConvertOrigin(client, fungibleStakedSuiId) : null,
    ]);
    const estimatedTotalCpcMist = rate
        ? poolTokensToCpcMist(poolTokenAmount, rate)
        : poolTokenAmount;
    const lockedPrincipalMist = poolData?.principalMist ?? null;
    const lockUntilEpoch = poolData?.lockUntilEpoch ?? null;
    let stakeActivationEpoch = poolData?.stakeActivationEpoch ?? null;
    if (convertOrigin != null && convertOrigin.stakeActivationEpoch > 0n) {
        stakeActivationEpoch = convertOrigin.stakeActivationEpoch;
    }
    let userPrincipalMist = null;
    let estimatedUnclaimedRewardMist = null;
    if (convertOrigin != null && convertOrigin.principalMist > 0n) {
        userPrincipalMist = convertOrigin.principalMist;
    }
    if (lockedPrincipalMist != null && rate && poolData && poolData.totalSupply > 0n) {
        if (userPrincipalMist == null) {
            userPrincipalMist = calculateFungibleWithdrawAmounts(rate, poolTokenAmount, lockedPrincipalMist, poolData.totalSupply).principalMist;
        }
        const drained = planFungibleRewardsDrain(rate, poolTokenAmount, lockedPrincipalMist, poolData.totalSupply).totalRewardsMist;
        estimatedUnclaimedRewardMist = drained < CLAIM_REWARDS_DUST_MIST ? 0n : drained;
    }
    else if (lockedPrincipalMist != null && poolData?.totalSupply === 0n) {
        if (userPrincipalMist == null)
            userPrincipalMist = 0n;
        estimatedUnclaimedRewardMist = 0n;
    }
    return {
        poolTokenAmount,
        estimatedTotalCpcMist,
        lockedPrincipalMist,
        userPrincipalMist,
        lockUntilEpoch,
        stakeActivationEpoch,
        estimatedUnclaimedRewardMist,
    };
}
/**
 * Currently drainable rewards for a StakedSui in one click: convert, then repeat
 * `rewards_only` until this epoch's surplus for the certificate is gone.
 */
export async function estimateStakedSuiNextClaimMist(client, poolId, principalMist, stakeActiveEpoch, currentEpoch) {
    if (principalMist <= 0n)
        return 0n;
    const [rateNow, rateActive, poolData] = await Promise.all([
        fetchPoolExchangeRateAtEpoch(client, poolId, currentEpoch),
        fetchPoolExchangeRateAtEpoch(client, poolId, stakeActiveEpoch),
        fetchFungiblePoolData(client, poolId),
    ]);
    if (!rateNow || !rateActive)
        return null;
    const minted = cpcMistToPoolTokens(principalMist, rateActive);
    if (minted <= 0n)
        return 0n;
    const newSupply = (poolData?.totalSupply ?? 0n) + minted;
    const newPrincipal = (poolData?.principalMist ?? 0n) + principalMist;
    const drained = planFungibleRewardsDrain(rateNow, minted, newPrincipal, newSupply).totalRewardsMist;
    return drained < CLAIM_REWARDS_DUST_MIST ? 0n : drained;
}
function fieldObjectId(fields, key) {
    return moveUid(fields?.[key] ?? null);
}
async function planDrainForTokens(client, poolId, tokenValue, epoch, fungibleStakedSuiId) {
    if (tokenValue <= 0n)
        return { totalRewardsMist: 0n, redeemCount: 0, remainingValue: 0n };
    const [rate, poolData] = await Promise.all([
        fetchPoolExchangeRateAtEpoch(client, poolId, epoch),
        fetchFungiblePoolData(client, poolId, fungibleStakedSuiId),
    ]);
    if (!rate || !poolData || poolData.totalSupply === 0n)
        return null;
    const plan = planFungibleRewardsDrain(rate, tokenValue, poolData.principalMist, poolData.totalSupply);
    if (plan.totalRewardsMist < CLAIM_REWARDS_DUST_MIST) {
        return { ...plan, totalRewardsMist: 0n, redeemCount: 0 };
    }
    return plan;
}
export async function planClaimRewardsDrainForFungible(client, fungibleStakedSuiId) {
    if (!client.getLatestSuiSystemState)
        return null;
    const [obj, system] = await Promise.all([
        client.getObject({ id: fungibleStakedSuiId, options: { showContent: true } }),
        client.getLatestSuiSystemState(),
    ]);
    const fields = obj.data?.content?.fields;
    const poolId = fieldObjectId(fields, 'pool_id');
    const value = u64Field(fields, 'value');
    if (!poolId || value == null)
        return null;
    return planDrainForTokens(client, poolId, value, Number(system.epoch), fungibleStakedSuiId);
}
export async function planClaimRewardsDrainForStakedSui(client, stakedSuiId) {
    if (!client.getLatestSuiSystemState)
        return null;
    const [obj, system] = await Promise.all([
        client.getObject({ id: stakedSuiId, options: { showContent: true } }),
        client.getLatestSuiSystemState(),
    ]);
    const fields = obj.data?.content?.fields;
    const poolId = fieldObjectId(fields, 'pool_id');
    const principal = u64Field(asMoveFields(fields?.principal), 'value') ?? u64Field(fields, 'principal');
    const activeEpoch = u64Field(fields, 'stake_activation_epoch');
    if (!poolId || principal == null || activeEpoch == null)
        return null;
    const epoch = Number(system.epoch);
    const [rateNow, rateActive, poolData] = await Promise.all([
        fetchPoolExchangeRateAtEpoch(client, poolId, epoch),
        fetchPoolExchangeRateAtEpoch(client, poolId, Number(activeEpoch)),
        fetchFungiblePoolData(client, poolId),
    ]);
    if (!rateNow || !rateActive)
        return null;
    const minted = cpcMistToPoolTokens(principal, rateActive);
    if (minted <= 0n)
        return { totalRewardsMist: 0n, redeemCount: 0, remainingValue: 0n };
    const newSupply = (poolData?.totalSupply ?? 0n) + minted;
    const newPrincipal = (poolData?.principalMist ?? 0n) + principal;
    const plan = planFungibleRewardsDrain(rateNow, minted, newPrincipal, newSupply);
    if (plan.totalRewardsMist < CLAIM_REWARDS_DUST_MIST) {
        return { ...plan, totalRewardsMist: 0n, redeemCount: 0 };
    }
    return plan;
}
export { fetchPoolExchangeRateAtEpoch };
